import type { Artifact, CompanyTree } from "@rakazo/contracts";

export type LibraryItem = Artifact & { versionCount: number; spaceId: string };
export type LibrarySource = { spaceId: string; botId?: string };
export type LibraryCursors = Record<string, string>;
export const libraryListingRoot = (userId: string | null) => ["library-list", userId] as const;

/** The company API supplies accessible departments; each fetch still uses its own authenticated space. */
export function librarySources(
  company: CompanyTree[number] | undefined,
  departmentId: string | null,
  botId: string | null,
): LibrarySource[] {
  if (!company) return [];
  const bot = botId ? company.bots.find((b) => b.id === botId) : undefined;
  if (botId && !bot) return [];
  return company.departments
    .filter((d) => !departmentId || d.id === departmentId)
    .flatMap((d) => {
      if (!bot) return [{ spaceId: d.id }];
      const context = bot.contexts.find((c) => c.departmentId === d.id && c.enabled);
      return context ? [{ spaceId: d.id, botId: context.botId }] : [];
    });
}

export async function loadLibraryPage(
  sources: readonly LibrarySource[],
  cursors: LibraryCursors | undefined,
  load: (
    source: LibrarySource,
    cursor: string | undefined,
  ) => Promise<{ items: Array<Artifact & { versionCount: number }>; nextCursor: string | null }>,
): Promise<{ items: LibraryItem[]; nextCursors: LibraryCursors }> {
  const selected = cursors ? sources.filter((s) => Object.hasOwn(cursors, s.spaceId)) : sources;
  const pages = await Promise.all(
    selected.map(async (source) => ({
      source,
      page: await load(source, cursors?.[source.spaceId]),
    })),
  );
  return {
    items: pages.flatMap(({ source, page }) =>
      page.items.map((item) => ({ ...item, spaceId: source.spaceId })),
    ),
    nextCursors: Object.fromEntries(
      pages.flatMap(({ source, page }) =>
        page.nextCursor ? [[source.spaceId, page.nextCursor]] : [],
      ),
    ),
  };
}

export function libraryItemPath(item: { id: string; isApp?: boolean; spaceId?: string }): string {
  const params = new URLSearchParams();
  if (item.isApp) params.set("type", "apps");
  if (item.spaceId) params.set("department", item.spaceId);
  return `/app/artifacts/${encodeURIComponent(item.id)}${params.size ? `?${params}` : ""}`;
}
